import { BATCH_TYPE_OPTIONS } from '@/components/Payouts/batchTypeOptions';
import { DATE_FORMAT } from '@/helpers/dates';
import { Box, DateInput, FormikCheckboxInput, FormikSelectInputNative } from '@palmetto/palmetto-components';
import { Field, useFormikContext } from 'formik';
import { DateTime } from 'luxon';
import { useCallback, useMemo } from 'react';

import { BatchType } from 'types';

export const CUTOFF_DISPLAY_FORMAT_WITH_TZ = 'MM-dd-yyyy hh:mm a z';
const CUTOFF_DISPLAY_FORMAT = 'MM-dd-yyyy hh:mm a';
const CUTOFF_DATE_TIME_LABEL = `Cutoff Date & Time (${DateTime.local().toFormat('ZZZZ')})`;
const AUTOFILL_DESCRIPTION_STYLE = { marginTop: '-8px' } as const;

export interface AdhocBatchFields {
  batchType: BatchType | '';
  batchDate: string;
  autoFill: boolean;
  cutoffDateTimeIso: string;
}

export const AdhocBatchFormFields = ({ selectionCount }: { selectionCount: number }) => {
  const { values, errors, touched, isSubmitting, setFieldValue } = useFormikContext<AdhocBatchFields>();

  const handleBatchDateChange = useCallback(
    (date: Date | [Date, Date] | null) => {
      if (!(date instanceof Date)) return;
      void setFieldValue('batchDate', DateTime.fromJSDate(date).toFormat(DATE_FORMAT));
    },
    [setFieldValue],
  );

  const handleAutoFillChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { checked } = event.target;
      void setFieldValue('autoFill', checked);
      if (!checked) void setFieldValue('cutoffDateTimeIso', '');
    },
    [setFieldValue],
  );

  const handleCutoffChange = useCallback(
    (date: Date | [Date, Date] | null) => {
      if (!(date instanceof Date)) return;
      void setFieldValue('cutoffDateTimeIso', DateTime.fromJSDate(date).toISO() ?? '');
    },
    [setFieldValue],
  );

  const cutoffDisplay = useMemo(
    () =>
      values.cutoffDateTimeIso
        ? DateTime.fromISO(values.cutoffDateTimeIso).toFormat(CUTOFF_DISPLAY_FORMAT_WITH_TZ)
        : '',
    [values.cutoffDateTimeIso],
  );

  const cutoffSelected = useMemo(
    () => (values.cutoffDateTimeIso ? DateTime.fromISO(values.cutoffDateTimeIso).toJSDate() : undefined),
    [values.cutoffDateTimeIso],
  );

  const cutoffDatePickerProps = useMemo(
    () => ({ selected: cutoffSelected, onChange: handleCutoffChange, showTimeSelect: true, maxDate: new Date() }),
    [cutoffSelected, handleCutoffChange],
  );

  const handleClearBatchDate = useCallback(() => setFieldValue('batchDate', ''), [setFieldValue]);
  const handleClearCutoff = useCallback(() => setFieldValue('cutoffDateTimeIso', ''), [setFieldValue]);

  const batchDatePickerProps = useMemo(() => ({ onChange: handleBatchDateChange }), [handleBatchDateChange]);
  const batchDateTextInputProps = useMemo(
    () => ({ name: 'batchDate', onClear: handleClearBatchDate }),
    [handleClearBatchDate],
  );
  const cutoffTextInputProps = useMemo(
    () => ({ name: 'cutoffDateTimeIso', onClear: handleClearCutoff }),
    [handleClearCutoff],
  );

  return (
    <Box childGap="lg">
      <Field
        label="Batch Type"
        name="batchType"
        id="batchType"
        component={FormikSelectInputNative}
        isRequired
        isDisabled={isSubmitting}
        options={BATCH_TYPE_OPTIONS}
        helpText="Each transaction lands in the batch matching its payee and payout event."
      />
      <DateInput
        label="Batch Date"
        name="batchDate"
        id="batchDate"
        placeholder="Pick a date"
        dateFormat={DATE_FORMAT}
        isRequired
        isDisabled={isSubmitting}
        width="2xl"
        value={values.batchDate}
        error={touched.batchDate && errors.batchDate ? errors.batchDate : undefined}
        datePickerProps={batchDatePickerProps}
        textInputProps={batchDateTextInputProps}
      />
      <Field
        label="Auto-populate from unbatched transactions"
        name="autoFill"
        id="autoFill"
        component={FormikCheckboxInput}
        onChange={handleAutoFillChange}
      />
      <Box color="body-secondary" fontSize="sm" style={AUTOFILL_DESCRIPTION_STYLE}>
        Pull in eligible unbatched transactions as of a cutoff date/time.
      </Box>
      {!values.autoFill && (
        <Box color="body-secondary" fontSize="sm">
          {selectionCount} transaction{selectionCount === 1 ? '' : 's'} selected
        </Box>
      )}
      {values.autoFill && (
        <DateInput
          label={CUTOFF_DATE_TIME_LABEL}
          name="cutoffDateTimeIso"
          id="cutoffDateTimeIso"
          placeholder="Pick a date & time"
          dateFormat={CUTOFF_DISPLAY_FORMAT}
          isDisabled={isSubmitting}
          width="2xl"
          value={cutoffDisplay}
          error={touched.cutoffDateTimeIso && errors.cutoffDateTimeIso ? errors.cutoffDateTimeIso : undefined}
          datePickerProps={cutoffDatePickerProps}
          textInputProps={cutoffTextInputProps}
        />
      )}
    </Box>
  );
};
